import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Users, MessageCircle, Network, Target, Globe, Heart } from 'lucide-react';

const Collaboration = () => {
  const collaborationAspects = [
    {
      icon: <MessageCircle className="h-8 w-8" />,
      title: "Open Communication",
      description: "We foster an environment where ideas flow freely, feedback is welcomed, and every voice is heard and valued."
    },
    {
      icon: <Network className="h-8 w-8" />,
      title: "Cross-Functional Teams",
      description: "We bring together diverse expertise to solve complex problems and create comprehensive solutions."
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: "Global Partnerships",
      description: "We collaborate with organizations worldwide to expand our impact and learn from different perspectives."
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Community Engagement",
      description: "We work closely with communities to understand their needs and develop solutions that truly serve them."
    }
  ];

  const collaborationBenefits = [
    {
      title: "Enhanced Creativity",
      description: "Diverse perspectives and experiences lead to more innovative solutions and breakthrough ideas.",
      impact: "3x more creative solutions"
    },
    {
      title: "Faster Problem Solving",
      description: "Collaborative teams identify issues faster and develop solutions more efficiently than isolated work.",
      impact: "50% faster resolution"
    },
    {
      title: "Improved Quality",
      description: "Multiple viewpoints and peer review processes result in higher quality outputs and fewer errors.",
      impact: "40% quality improvement"
    },
    {
      title: "Stronger Relationships",
      description: "Working together builds trust, understanding, and long-term partnerships that benefit all parties.",
      impact: "95% partner satisfaction"
    }
  ];

  const collaborationTools = [
    "Real-time communication platforms for instant feedback and discussion",
    "Shared project management systems for transparent progress tracking",
    "Collaborative development environments for code sharing and review", 
    "Virtual meeting spaces for global team coordination",
    "Knowledge sharing platforms for collective learning and growth",
    "Community feedback channels for continuous user input"
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/">
          <Button variant="outline" className="mb-8 text-vincotek-blue border-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Button>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-8">
              <Users className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Collaboration
            </h1>
            <p className="text-xl text-vincotek-blue font-semibold mb-6">
              Working Together for Meaningful Progress
            </p>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              We believe that the best solutions emerge from collaborative efforts where diverse perspectives, 
              expertise, and experiences come together. Collaboration isn't just how we work—it's how we create 
              value that no individual could achieve alone.
            </p>
            
            <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-8 border border-gray-100">
              <blockquote className="text-2xl font-medium text-vincotek-dark italic mb-4">
                "Collaboration multiplies talent and divides effort."
              </blockquote>
              <p className="text-gray-600">
                — The foundation of Vincotek's approach to teamwork
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Collaboration Aspects */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              How We Collaborate
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our collaborative approach spans multiple dimensions, from internal teamwork to external partnerships.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {collaborationAspects.map((aspect, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                <CardContent className="p-8">
                  <div className="text-vincotek-blue mb-6 group-hover:scale-110 transition-transform duration-300">
                    {aspect.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    {aspect.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {aspect.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Collaboration Benefits */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              The Power of Collaboration
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Collaborative approaches consistently deliver better outcomes for our clients, our team, and our community.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {collaborationBenefits.map((benefit, index) => (
              <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-vincotek-dark">
                      {benefit.title}
                    </h3>
                    <span className="text-sm font-bold text-vincotek-blue bg-vincotek-blue/10 px-3 py-1 rounded-full">
                      {benefit.impact}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Collaboration Tools */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
                Collaboration Infrastructure
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We've invested in tools and processes that make collaboration seamless, effective, and inclusive.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-vincotek-dark to-gray-800 rounded-2xl p-8 text-white">
              <div className="flex items-center mb-6">
                <Target className="h-8 w-8 text-vincotek-yellow mr-4" />
                <h3 className="text-2xl font-semibold">Our Collaboration Tools</h3>
              </div>
              
              <div className="space-y-4">
                {collaborationTools.map((tool, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-3 h-3 bg-vincotek-yellow rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-gray-300 leading-relaxed">{tool}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Invitation */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Collaborate With Us
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Whether you're a potential client, partner organization, or like-minded professional, 
              we're always interested in exploring collaborative opportunities that create mutual value 
              and positive impact.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                  Start a Conversation
                </Button>
              </Link>
              <Link to="/about/leadership">
                <Button variant="outline" className="border-vincotek-yellow text-vincotek-dark hover:bg-vincotek-yellow px-8 py-3 rounded-full transition-all duration-300">
                  Meet Our Team
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Collaboration;